/**
 * VukzAssistPanel — Embedded conversational chat panel for VUKZ Assist agents.
 *
 * This is a SIDEBAR PANEL (not modal/floating). Placed in the 1/3 sidebar column
 * of role-specific Home Pages. Communicates with the Foundry agent via
 * the backend proxy (role → path mapping in assistClient.js).
 *
 * Features:
 *   - Multi-turn conversation (thread persistence)
 *   - Quick prompt chips (role-aware: Coach, Athlete, Prep, Gestor)
 *   - ACTION rendering (navigate, toast, links)
 *   - CHAT rendering (Markdown → formatted HTML)
 *   - Sticky input at bottom
 *   - VUKZ PB theme
 *
 * Props:
 *   role  — 'ATHLETE' | 'COACH' | 'ATLETA' | 'TREINADOR' | 'PREPARADOR_FISICO' | 'PREP' | 'GESTOR' | 'PERFORMANCE_MANAGER'
 *   sport — default 'swimming'
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MessageCircle, Send, Loader2, Sparkles, RotateCcw,
  Zap, BookOpen, Dumbbell, BarChart3, Heart,
  AlertCircle, ThumbsUp, ThumbsDown, Mic, Square, X, AudioLines,
} from 'lucide-react';
import { VUKZ } from '../../theme/vukzTheme';
import { useAuth } from '../../contexts/AuthContext';
import { useSportsLang } from '../../contexts/SportsLangContext';
import { useAssistFormBridge } from '../../contexts/AssistFormBridge';
import { useAssistOptional } from '../../contexts/AssistContext';
import { sendAssistMessage, resetAssistThread, submitAssistFeedback, fetchTargetContext, transcribeAudio, checkRealtimeHealth, createRealtimeSession, sendRealtimeTurn } from './assistClient';
import { RealtimeClient, RealtimeStatus, isRealtimeSupported } from './realtimeClient';
import { buildUiReferenceContext } from './uiReferenceContext';
import { getSession, saveSession, clearSession, subscribeSession } from './assistSession';
import { renderMarkdown, executeAction, describeAction, runImmediateEffect, navigationExecutionPolicy } from './renderers';
import ProposalBubble from './ProposalBubble';

// Contract v1: comando local de undo — nunca vai ao LLM (as inversas já
// estão armazenadas na Bridge). Escopo deliberadamente estreito.
const UNDO_COMMAND_RE = /^\s*(desfaz|desfazer|desfaz isso|undo)\s*[.!]?\s*$/i;

/* ─── i18n Labels ─────────────────────────────────────── */
const LABELS = {
  pt: {
    roleLabel: { TREINADOR: 'Treinador', ATLETA: 'Atleta', PREP: 'Preparador Físico', GESTOR: 'Gestor', BIOMECANICO: 'Biomecânico', NUTRICIONISTA: 'Nutricionista' },
    emptyState: {
      TREINADOR: 'Como posso ajudar com o treino?',
      ATLETA: 'Como posso ajudar hoje?',
      PREP: 'Como posso ajudar com a preparação?',
      GESTOR: 'Como posso ajudar com a gestão?',
      BIOMECANICO: 'Análise técnica, drills, observações...',
      NUTRICIONISTA: 'Plano nutricional, cálculos, suplementação...',
    },
    evidence: 'Baseado em evidências da Biblioteca VUKZ',
    typing: 'VUKZ está pensando...',
    actionFallback: 'Ação executada',
    feedbackPos: '👍 Obrigado!',
    feedbackNeg: '👎 Obrigado pelo feedback',
    titleReset: 'Nova conversa',
    titleThumbUp: 'Resposta útil',
    titleThumbDown: 'Resposta não ajudou',
    errorComm: 'Erro de comunicação',
    errorMsg: 'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.',
    errorContent: '⚠️ Erro de comunicação com o agente. Tente novamente.',
    undoDone: 'Desfeito. O formulário voltou ao estado anterior à última aplicação (nada foi salvo).',
    undoUnavailable: 'Não há aplicação para desfazer, ou o formulário mudou depois dela.',
    placeholder: {
      TREINADOR: 'Criar treino, buscar protocolo...',
      ATLETA: 'Perguntar sobre treino...',
      PREP: 'Wellness, carga, recuperação...',
      GESTOR: 'Compliance, estratégia, decisões...',
      BIOMECANICO: 'Analisar técnica, prescrever drill...',
      NUTRICIONISTA: 'Cálculos, plano alimentar...',
    },
    footer: 'Respostas baseadas na Biblioteca VUKZ (81 Method Cards)',
    promptLabels: {
      coach: ['Criar treino velocidade', 'Buscar protocolo USRPT', 'Analisar carga semanal', 'Verificar wellness'],
      athlete: ['Explicar meu treino', 'Resumo da semana', 'Meu corpo está bem?', 'Estou evoluindo?', 'Responder treinador'],
      prep: ['Wellness do elenco', 'Carga de treino', 'Protocolo recuperação', 'Compliance do elenco'],
      gestor: ['Overview do elenco', 'Atletas em risco', 'Compliance baixa', 'Decisão estratégica'],
      execution: ['Marcar série feita', 'Quanto falta?', 'Adicionar nota', 'Concluir treino'],
      biomecanico: ['Analisar técnica', 'Prescrever drill', 'Escrever observação', 'Checklist pré-prova'],
      nutricionista: ['Calcular calorias', 'Plano de refeições', 'Dia de competição', 'Suplementação'],
    },
  },
  en: {
    roleLabel: { TREINADOR: 'Coach', ATLETA: 'Athlete', PREP: 'Strength Coach', GESTOR: 'Manager', BIOMECANICO: 'Biomechanist', NUTRICIONISTA: 'Nutritionist' },
    emptyState: {
      TREINADOR: 'How can I help with training?',
      ATLETA: 'How can I help today?',
      PREP: 'How can I help with preparation?',
      GESTOR: 'How can I help with management?',
      BIOMECANICO: 'Technique analysis, drills, observations...',
      NUTRICIONISTA: 'Nutrition plan, calculations, supplements...',
    },
    evidence: 'Based on VUKZ Library evidence',
    typing: 'VUKZ is thinking...',
    actionFallback: 'Action executed',
    feedbackPos: '👍 Thanks!',
    feedbackNeg: '👎 Thanks for the feedback',
    titleReset: 'New conversation',
    titleThumbUp: 'Helpful response',
    titleThumbDown: "Response wasn't helpful",
    errorComm: 'Communication error',
    errorMsg: 'Sorry, an error occurred processing your message. Please try again.',
    errorContent: '⚠️ Communication error with agent. Please try again.',
    undoDone: 'Undone. The form is back to the state before the last apply (nothing was saved).',
    undoUnavailable: 'Nothing to undo, or the form changed after the last apply.',
    placeholder: {
      TREINADOR: 'Create workout, search protocol...',
      ATLETA: 'Ask about training...',
      PREP: 'Wellness, load, recovery...',
      GESTOR: 'Compliance, strategy, decisions...',
      BIOMECANICO: 'Analyze technique, prescribe drill...',
      NUTRICIONISTA: 'Calculations, meal plan...',
    },
    footer: 'Responses based on VUKZ Library (81 Method Cards)',
    promptLabels: {
      coach: ['Create speed workout', 'Find USRPT protocol', 'Analyze weekly load', 'Check wellness'],
      athlete: ['Explain my workout', 'Weekly summary', 'Is my body OK?', 'Am I improving?', 'Reply to coach'],
      prep: ['Squad wellness', 'Training load', 'Recovery protocol', 'Squad compliance'],
      gestor: ['Squad overview', 'At-risk athletes', 'Low compliance', 'Strategic decision'],
      execution: ['Mark set done', 'How much left?', 'Add note', 'Finish workout'],
      biomecanico: ['Analyze technique', 'Prescribe drill', 'Write observation', 'Pre-race checklist'],
      nutricionista: ['Calculate calories', 'Meal plan', 'Race day nutrition', 'Supplementation'],
    },
  },
};

/* ─── Quick Prompts per role ──────────────────────────── */
const COACH_PROMPTS = [
  { label: 'Criar treino velocidade', icon: Dumbbell, text: 'Crie um treino de velocidade 50m livre para fase build, volume ~3000m' },
  { label: 'Buscar protocolo USRPT', icon: BookOpen, text: 'Busque na biblioteca protocolos USRPT para velocistas' },
  { label: 'Analisar carga semanal', icon: BarChart3, text: 'Analise a carga de treino semanal dos meus atletas' },
  { label: 'Verificar wellness', icon: Heart, text: 'Verifique o wellness mais recente dos atletas antes do treino de hoje' },
];

const ATHLETE_PROMPTS = [
  { label: 'Explicar meu treino', icon: Dumbbell, text: 'Explique o treino de hoje de forma simples: o que fazer, como e por quê' },
  { label: 'Resumo da semana', icon: BarChart3, text: 'Faça um resumo da minha semana: treinos, carga, wellness e evolução' },
  { label: 'Meu corpo está bem?', icon: Heart, text: 'Como está meu corpo agora? Fadiga, carga, sono — posso treinar forte hoje?' },
  { label: 'Estou evoluindo?', icon: Sparkles, text: 'Como está meu progresso nas últimas 4 semanas? Tempos, volume, consistência' },
  { label: 'Responder treinador', icon: BookOpen, text: 'Me ajude a redigir uma mensagem para o treinador sobre como me senti no treino' },
];

const PREP_PROMPTS = [
  { label: 'Wellness do elenco', icon: Heart, text: 'Qual o status de wellness do elenco nas últimas 48h? Algum atleta em risco?' },
  { label: 'Carga de treino', icon: Dumbbell, text: 'Analise a carga de treino semanal e identifique atletas com sobrecarga ou subcarga' },
  { label: 'Protocolo recuperação', icon: BookOpen, text: 'Busque protocolos de recuperação pós-competição para velocistas' },
  { label: 'Compliance do elenco', icon: BarChart3, text: 'Qual o percentual de compliance dos atletas nos últimos 7 dias?' },
];

const GESTOR_PROMPTS = [
  { label: 'Overview do elenco', icon: BarChart3, text: 'Dê uma visão geral da compliance e wellness do elenco' },
  { label: 'Atletas em risco', icon: Heart, text: 'Quais atletas estão com fadiga elevada ou flags de lesão nas últimas 48h?' },
  { label: 'Compliance baixa', icon: Dumbbell, text: 'Quais atletas têm compliance abaixo de 70% nos últimos 7 dias?' },
  { label: 'Decisão estratégica', icon: BookOpen, text: 'Preciso tomar uma decisão sobre a periodização do próximo mesociclo. Quais dados considerar?' },
];

const EXECUTION_PROMPTS = [
  { label: 'Marcar série feita', icon: Zap, text: 'Marque a próxima série como concluída' },
  { label: 'Quanto falta?', icon: BarChart3, text: 'Qual o progresso atual da execução? Quantas séries faltam?' },
  { label: 'Adicionar nota', icon: BookOpen, text: 'Adicione uma nota técnica no item atual' },
  { label: 'Concluir treino', icon: Heart, text: 'Concluir a execução do treino com RPE e observações' },
];

const BIOMECANICO_PROMPTS = [
  { label: 'Analisar técnica', icon: Sparkles, text: 'Analise a técnica do atleta selecionado com base nas sessões SwimVision mais recentes' },
  { label: 'Prescrever drill', icon: Dumbbell, text: 'Recomende drills educativos para os padrões técnicos identificados nas últimas análises' },
  { label: 'Escrever observação', icon: BookOpen, text: 'Me ajude a redigir observações técnicas para o treinador, preparador físico e atleta' },
  { label: 'Checklist pré-prova', icon: BarChart3, text: 'Crie um checklist biomecânico pré-competição para o atleta selecionado' },
];

const NUTRICIONISTA_PROMPTS = [
  { label: 'Calcular calorias', icon: BarChart3, text: 'Calcule as necessidades calóricas do atleta selecionado considerando o volume de treino atual' },
  { label: 'Plano de refeições', icon: BookOpen, text: 'Crie um cardápio para o dia de treino pesado com macros adequados para a periodização atual' },
  { label: 'Dia de competição', icon: Sparkles, text: 'Qual a estratégia nutricional para o dia de competição? Horários de refeição e hidratação' },
  { label: 'Suplementação', icon: Dumbbell, text: 'Quais suplementos são recomendados para a fase de treino atual? Creatina, cafeína, colágeno?' },
];

/* ─── Message Bubble ──────────────────────────────────── */
const MessageBubble = ({ msg, onFeedback }) => {
  const isUser = msg.role === 'user';
  const isAction = msg.response?.type === 'ACTION';
  const [feedbackSent, setFeedbackSent] = useState(null);
  const { lang } = useSportsLang();
  const T = LABELS[lang] || LABELS.pt;

  const handleFeedback = async (type) => {
    setFeedbackSent(type);
    if (onFeedback) onFeedback(msg, type);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[90%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
          isUser
            ? 'bg-neutral-900 text-white rounded-br-sm'
            : 'bg-white border rounded-bl-sm'
        }`}
        style={!isUser ? { borderColor: VUKZ.border } : undefined}
      >
        {isUser ? (
          <span>{msg.text}</span>
        ) : (msg.response?.type === 'ASSIST' || (msg.v2Actions && msg.v2Actions.length > 0)) ? (
          /* ── ASSIST v2 (C2): UM turno = fala + 0..N actions abaixo,
             na ordem original. Nunca gerar bubbles separados por action.
             NAV-2: envelope v1 do Coach com action de navegação também
             entra aqui (CTA) — mesma renderização, zero auto-navigate. */
          <div>
            {(msg.response.message || msg.response?.payload?.message || msg.response?.content) ? (
              <div
                className="assist-markdown"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.response.message || msg.response?.payload?.message || msg.response?.content) }}
              />
            ) : null}
            {(msg.v2Actions || []).map((va, i) => {
              if (va.desc.kind === 'ignored') return null;
              const divider = (msg.response.message || i > 0)
                ? 'mt-3 pt-3 border-t' : '';
              return (
                <div key={i} className={divider} style={divider ? { borderColor: VUKZ.border } : undefined}>
                  {va.desc.kind === 'fillForm' ? (
                    <div>
                      {va.proposal ? (
                        <ProposalBubble proposal={va.proposal} />
                      ) : (
                        <FillFormBubble fillForm={va.desc.fillForm} onApply={msg.onFillForm} />
                      )}
                      {va.desc.selection && (
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: VUKZ.border }}>
                          <SelectionBubble selection={va.desc.selection} onSelect={msg.onSelect} />
                        </div>
                      )}
                    </div>
                  ) : va.desc.kind === 'selection' ? (
                    <SelectionBubble selection={va.desc.selection} onSelect={msg.onSelect} />
                  ) : va.desc.kind === 'cta' ? (
                    <div>
                      {va.desc.label && (
                        <div className="flex items-center gap-2 pb-1.5">
                          <Zap size={14} className="text-amber-500 shrink-0" />
                          <span className="text-sm font-medium" style={{ color: VUKZ.text }}>{va.desc.label}</span>
                        </div>
                      )}
                      <button
                        onClick={() => msg.onCta && msg.onCta(va.desc.cta)}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: VUKZ.primary }}
                      >
                        {va.desc.cta.label}
                      </button>
                    </div>
                  ) : va.desc.kind === 'autoNavigate' ? (
                    <div className="flex items-center gap-2 py-1" data-testid="assist-autonav">
                      <Zap size={14} className="text-amber-500 shrink-0" />
                      <span className="text-sm font-medium" style={{ color: VUKZ.text }}>{va.desc.label}</span>
                    </div>
                  ) : va.desc.kind === 'echo' ? (
                    <div className="flex items-center gap-2 py-1">
                      <Zap size={14} className="text-amber-500 shrink-0" />
                      <span className="text-sm font-medium" style={{ color: VUKZ.text }}>{va.desc.label}</span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : msg.actionResult?.fillForm ? (
          <div>
            {msg.proposal ? (
              <ProposalBubble
                proposal={msg.proposal}
                message={msg.response?.payload?.message || msg.response?.content}
              />
            ) : (
            <FillFormBubble
              fillForm={msg.actionResult.fillForm}
              message={msg.response?.payload?.message || msg.response?.content}
              onApply={msg.onFillForm}
            />
            )}
            {msg.actionResult?.selection && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: VUKZ.border }}>
                <SelectionBubble
                  selection={msg.actionResult.selection}
                  onSelect={msg.onSelect}
                />
              </div>
            )}
          </div>
        ) : msg.actionResult?.selection ? (
          <SelectionBubble
            selection={msg.actionResult.selection}
            onSelect={msg.onSelect}
          />
        ) : isAction ? (
          <ActionBubble response={msg.response} actionResult={msg.actionResult} />
        ) : (
          <div
            className="assist-markdown"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.response?.content || msg.text) }}
          />
        )}
        {msg.meta?.response_time_ms && (
          <div className="mt-1.5 text-[10px] opacity-40">
            {msg.meta.response_time_ms}ms
          </div>
        )}
        {/* Feedback buttons — only on assistant messages */}
        {!isUser && (
          <div className="mt-1.5 flex items-center gap-1">
            {feedbackSent ? (
              <span className="text-[10px] opacity-50">
                {feedbackSent === 'positive' ? T.feedbackPos : T.feedbackNeg}
              </span>
            ) : (
              <>
                <button
                  onClick={() => handleFeedback('positive')}
                  className="p-1 rounded hover:bg-green-50 transition-colors opacity-40 hover:opacity-100"
                  title={T.titleThumbUp}
                >
                  <ThumbsUp size={12} className="text-green-600" />
                </button>
                <button
                  onClick={() => handleFeedback('negative')}
                  className="p-1 rounded hover:bg-red-50 transition-colors opacity-40 hover:opacity-100"
                  title={T.titleThumbDown}
                >
                  <ThumbsDown size={12} className="text-red-500" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Action Bubble ───────────────────────────────────── */
const ActionBubble = ({ response, actionResult }) => {
  const { lang } = useSportsLang();
  const T = LABELS[lang] || LABELS.pt;
  return (
  <div className="flex items-center gap-2 py-1">
    <Zap size={14} className="text-amber-500 shrink-0" />
    <span className="text-sm font-medium" style={{ color: VUKZ.text }}>
      {actionResult?.label || response?.payload?.message || T.actionFallback}
    </span>
  </div>
  );
};

/* ─── Fill Form Bubble (FILL_FORM v1) ─────────────────────
   Card de revisão do patch sugerido pelo agente. NADA é aplicado ao
   formulário até o usuário clicar em Aplicar — e o save continua sendo
   o submit normal da página (API canônica). */
const FillFormBubble = ({ fillForm, message, onApply }) => {
  const [status, setStatus] = useState(null); // null | 'applied' | 'dismissed'
  // Resultado da validação de DOMÍNIO (applyAssistPatch/applyWellnessPatch)
  // devolvido pelo onApply do host — rejeições nunca são silenciosas.
  const [applyResult, setApplyResult] = useState(null);
  const { lang } = useSportsLang();
  const pt = lang !== 'en';
  const entries = Object.entries(fillForm.patch || {});

  if (status === 'applied') {
    const nApplied = applyResult?.applied?.length ?? 0;
    const rejectedList = applyResult?.rejected || [];
    // Continuidade (27/08): "revise e salve" SÓ no fill_form final
    // (READY_FOR_REVIEW). Intermediário: "aplicado" + a conversa segue
    // (o followup/chips já está renderizado logo abaixo na mesma bolha).
    const isFinal = fillForm.final === true;
    const appliedText = isFinal
      ? (pt ? 'Treino pronto para revisão — revise no formulário e salve.'
            : 'Workout ready for review — review the form and save.')
      : (applyResult
        ? (pt ? `${nApplied} campo(s) aplicado(s).` : `${nApplied} field(s) applied.`)
        : (pt ? 'Aplicado ao formulário.' : 'Applied to the form.'));
    return (
      <div className="py-1">
        <div className="flex items-center gap-2 text-sm font-medium text-green-700">
          <Zap size={14} className="shrink-0" />
          {appliedText}
        </div>
        {rejectedList.length > 0 && (
          <div className="mt-1 text-xs text-red-600">
            {(pt ? 'Recusado pelo formulário: ' : 'Rejected by the form: ')}
            {rejectedList.map((r) => `${r.field} (${r.reason})`).join(' · ')}
          </div>
        )}
      </div>
    );
  }
  if (status === 'dismissed') {
    return (
      <div className="py-1 text-sm opacity-60">
        {pt ? 'Sugestão descartada.' : 'Suggestion dismissed.'}
      </div>
    );
  }

  return (
    <div className="py-1">
      <div className="flex items-center gap-2 mb-2">
        <Zap size={14} className="text-amber-500 shrink-0" />
        <span className="text-sm font-semibold" style={{ color: VUKZ.text }}>
          {message || (pt ? 'Preparei uma sugestão de preenchimento' : 'I prepared a fill suggestion')}
        </span>
      </div>
      <div className="rounded-lg border bg-neutral-50 px-3 py-2 mb-2 max-h-48 overflow-auto" style={{ borderColor: VUKZ.border }}>
        {entries.map(([field, value]) => (
          <div key={field} className="flex justify-between gap-3 text-xs py-0.5">
            <span className="font-medium opacity-70">{field}</span>
            <span className="text-right break-all">
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </span>
          </div>
        ))}
      </div>
      {fillForm.missingFields?.length > 0 && (
        <div className="text-xs text-amber-700 mb-1">
          {(pt ? 'Faltou informar: ' : 'Missing: ') + fillForm.missingFields.join(', ')}
        </div>
      )}
      {fillForm.warnings?.length > 0 && (
        <div className="text-xs text-red-600 mb-1">{fillForm.warnings.join(' · ')}</div>
      )}
      <div className="flex gap-2 mt-1">
        <button
          onClick={() => {
            // onApply retorna { applied, rejected } da validação de domínio.
            // NENHUM POST/PUT acontece aqui — só estado React do formulário.
            const result = onApply ? onApply(fillForm.patch, fillForm) : null;
            setApplyResult(result || null);
            setStatus('applied');
          }}
          disabled={!onApply}
          className="px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-xs font-bold disabled:opacity-40"
        >
          {pt ? 'Aplicar ao formulário' : 'Apply to form'}
        </button>
        <button
          onClick={() => setStatus('dismissed')}
          className="px-3 py-1.5 rounded-lg border text-xs font-semibold"
          style={{ borderColor: VUKZ.border }}
        >
          {pt ? 'Ignorar' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
};

/* ─── Selection Bubble (request_selection) ────────────────
   Workflow CREATE_TRAINING: chips com opções REAIS do backend (IDs do
   banco). Ao clicar: (1) a página aplica a seleção via ponte confiável
   (applySelection — formulário preenche na hora), (2) o chat continua
   com uma mensagem estruturada para o próximo slot. */
const SelectionBubble = ({ selection, onSelect }) => {
  const [chosen, setChosen] = useState(null);
  // multi-select (SETUP_CLUB): Set de ids marcados antes de aplicar
  const [picked, setPicked] = useState(() => new Set());
  const { lang } = useSportsLang();
  const pt = lang !== 'en';
  const multiple = selection.selectionMode === 'multiple';
  const min = selection.min ?? 1;
  const max = selection.max ?? selection.options.length;

  if (chosen) {
    const label = Array.isArray(chosen)
      ? chosen.map((o) => o.label).join(', ')
      : chosen.label;
    return (
      <div className="py-1 flex items-center gap-2 text-sm font-medium text-green-700">
        <Zap size={14} className="shrink-0" />
        {(pt ? 'Selecionado: ' : 'Selected: ') + label}
      </div>
    );
  }

  const togglePick = (opt) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(opt.id)) next.delete(opt.id);
      else if (next.size < max) next.add(opt.id);
      return next;
    });
  };

  const applyPicked = () => {
    const opts = selection.options.filter((o) => picked.has(o.id));
    if (opts.length < min) return;
    setChosen(opts);
    onSelect && onSelect(opts, selection);
  };

  return (
    <div className="py-1">
      <div className="text-sm font-semibold mb-2" style={{ color: VUKZ.text }}>
        {selection.question}
      </div>
      {selection.warnings?.length > 0 && (
        <div className="mb-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          {selection.warnings.join(' ')}
        </div>
      )}
      {/* Pipeline TARGET: grupos (Atletas / Subgrupos) quando o backend envia */}
      {selection.groups?.length > 0 ? (
        <div className="space-y-2">
          {selection.groups.map((group) => (
            <div key={group.label}>
              <div className="text-[11px] font-bold uppercase tracking-wide mb-1" style={{ color: VUKZ.textLight }}>
                {group.label}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {group.options.map((opt) => (
                  <button
                    key={`${opt.type}-${opt.id}`}
                    onClick={() => { setChosen(opt); onSelect && onSelect(opt, selection); }}
                    className="px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors bg-white hover:bg-neutral-50"
                    style={{ borderColor: VUKZ.border, color: VUKZ.text }}
                  >
                    {opt.label}
                    {opt.sublabel && (
                      <span className="ml-1 font-normal opacity-50">{opt.sublabel}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="flex flex-wrap gap-1.5">
        {selection.options.map((opt) => {
          const active = multiple && picked.has(opt.id);
          return (
            <button
              key={`${opt.type}-${opt.id}`}
              onClick={() => {
                if (multiple) { togglePick(opt); return; }
                setChosen(opt); onSelect && onSelect(opt, selection);
              }}
              className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
                active ? 'bg-neutral-900 text-white' : 'bg-white hover:bg-neutral-50'
              }`}
              style={active ? { borderColor: VUKZ.text } : { borderColor: VUKZ.border, color: VUKZ.text }}
            >
              {opt.label}
              {opt.sublabel && (
                <span className="ml-1 font-normal opacity-50">{opt.sublabel}</span>
              )}
            </button>
          );
        })}
      </div>
      )}
      {multiple && (
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={applyPicked}
            disabled={picked.size < min}
            className="px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-xs font-bold disabled:opacity-40"
          >
            {pt ? `Aplicar seleção (${picked.size})` : `Apply selection (${picked.size})`}
          </button>
          <span className="text-[11px]" style={{ color: VUKZ.textLight }}>
            {pt ? `mín. ${min} · máx. ${max}` : `min ${min} · max ${max}`}
          </span>
        </div>
      )}
    </div>
  );
};

/* ─── Typing Indicator ────────────────────────────────── */
const TypingIndicator = () => {
  const { lang } = useSportsLang();
  const T = LABELS[lang] || LABELS.pt;
  return (
  <div className="flex justify-start mb-3">
    <div
      className="rounded-xl px-4 py-3 bg-white border flex items-center gap-2"
      style={{ borderColor: VUKZ.border }}
    >
      <Loader2 size={14} className="animate-spin" style={{ color: VUKZ.primary }} />
      <span className="text-xs" style={{ color: VUKZ.textLight }}>
        {T.typing}
      </span>
    </div>
  </div>
  );
};

/* ═══════════════════════════════════════════════════════ */
/* MAIN PANEL                                             */
/* ═══════════════════════════════════════════════════════ */
const VukzAssistPanel = ({
  role = 'COACH',
  sport = 'swimming',
  executionContext = null,
  // Nova prop: injectedPrompt injeta um prompt via quick actions da VukzAssistPage
  injectedPrompt = null,
  // Nova prop: promptSuggestions sobrescreve os prompts padrão (optional)
  promptSuggestions = null,
  // Nova prop: contexto de modo (para ajustar mensagem de empty state)
  contextMode = null,
  // Nova prop: guardrails de segurança passados pela VukzAssistPage (informativo)
  guardRails = null,
  // FILL_FORM v1: form_context do formulário aberto (form_id, schema_version,
  // current_values, missing_required_fields, allowed_values)
  formContext = null,
  // FILL_FORM v1: callback (patch, fillForm) chamado SOMENTE após o usuário
  // confirmar no card de revisão — a página aplica o patch ao formulário
  onFillForm = null,
  // Single Assist: ocupa 100% da altura do container (drawer global)
  fluid = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // AssistFormBridge — formulário ativo registrado pela página. Quando o
  // panel NÃO recebe formContext/onFillForm por prop (caso do Assist
  // global), busca o formulário ativo na ponte em tempo de envio.
  const {
    getActiveForm, activeFormId,
    captureSendContext, proposalFromFillForm, canUndo, undoLastAppliedProposal,
  } = useAssistFormBridge();
  const { lang } = useSportsLang();
  const T = LABELS[lang] || LABELS.pt;
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ── Role normalization (6 roles) ──────────────────
  const upperRole = (role || '').toUpperCase();
  const isCoach = ['COACH', 'TREINADOR'].includes(upperRole);
  const isAthlete = ['ATHLETE', 'ATLETA'].includes(upperRole);
  const isPrep = ['PREPARADOR_FISICO', 'PREP'].includes(upperRole);
  const isGestor = ['GESTOR', 'PERFORMANCE_MANAGER'].includes(upperRole);
  const isBiomecanico = ['BIOMECANICO', 'BIOMECÂNICO', 'BIOMECHANICS_SPECIALIST'].includes(upperRole);
  const isNutricionista = ['NUTRICIONISTA', 'NUTRI'].includes(upperRole);

  const normalizedRole = isCoach ? 'TREINADOR'
    : isAthlete ? 'ATLETA'
    : isPrep ? 'PREPARADOR_FISICO'
    : isGestor ? 'GESTOR'
    : isBiomecanico ? 'BIOMECANICO'
    : isNutricionista ? 'NUTRICIONISTA'
    : 'TREINADOR';

  const agentNameForRole = isCoach ? 'coach_assist'
    : isAthlete ? 'athlete_assist'
    : isPrep ? 'strength_assist'
    : isGestor ? 'perf_manager'
    : isBiomecanico ? 'biomech_assist'
    : isNutricionista ? 'nutrition_assist'
    : 'coach_assist';

  const roleDisplayKey = isCoach ? 'TREINADOR'
    : isAthlete ? 'ATLETA'
    : isPrep ? 'PREP'
    : isGestor ? 'GESTOR'
    : isBiomecanico ? 'BIOMECANICO'
    : isNutricionista ? 'NUTRICIONISTA'
    : 'TREINADOR';
  const roleLabel = T.roleLabel[roleDisplayKey];

  // Prompts with translated labels (text kept in PT — sent to AI agent)
  const promptsRaw = executionContext
    ? EXECUTION_PROMPTS
    : isCoach ? COACH_PROMPTS
    : isAthlete ? ATHLETE_PROMPTS
    : isPrep ? PREP_PROMPTS
    : isGestor ? GESTOR_PROMPTS
    : isBiomecanico ? BIOMECANICO_PROMPTS
    : isNutricionista ? NUTRICIONISTA_PROMPTS
    : COACH_PROMPTS;
  const promptLabelKey = executionContext ? 'execution'
    : isCoach ? 'coach'
    : isAthlete ? 'athlete'
    : isPrep ? 'prep'
    : isGestor ? 'gestor'
    : isBiomecanico ? 'biomecanico'
    : isNutricionista ? 'nutricionista'
    : 'coach';
  const prompts = promptsRaw.map((p, i) => ({
    ...p,
    label: T.promptLabels[promptLabelKey]?.[i] ?? p.label,
  }));

  const hasThreadPersistence = !isCoach;

  // ── State ─────────────────────────────────────
  // Sessão COMPARTILHADA (Single Assist): drawer, /assist e widgets
  // continuam a MESMA conversa — estado inicial vem do assistSession e
  // toda mudança é persistida lá. Nova conversa só via Reset.
  const initialSession = getSession(normalizedRole);
  const [messages, setMessages] = useState(initialSession.messages);
  // CANVAS-ENGINE-1: contexto opcional (painel também vive fora do shell).
  const assistCtx = useAssistOptional();
  const applyWorkspaceRef = useRef(null);
  useEffect(() => { applyWorkspaceRef.current = assistCtx?.applyWorkspace || null; }, [assistCtx]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(initialSession.threadId);
  const [error, setError] = useState(null);
  const [showPrompts, setShowPrompts] = useState(initialSession.messages.length === 0);

  useEffect(() => {
    saveSession(normalizedRole, { threadId, messages });
  }, [normalizedRole, threadId, messages]);

  // Live-sync entre superfícies (drawer global + embedded no builder):
  // outra instância salvou → esta reflete. setState com a MESMA referência
  // é no-op no React, então o ciclo save→notify→set→save termina sozinho.
  useEffect(() => {
    const unsub = subscribeSession((role, session) => {
      if (role !== normalizedRole) return;
      setMessages(session.messages);
      setThreadId(session.threadId);
    });
    return unsub;
  }, [normalizedRole]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Injected prompt from quick actions (VukzAssistPage)
  const lastInjectedRef = useRef(null);
  useEffect(() => {
    if (!injectedPrompt || !injectedPrompt.text) return;
    // Use ts to detect new injection (avoid re-firing on re-render)
    const key = `${injectedPrompt.text}:${injectedPrompt.ts}`;
    if (lastInjectedRef.current === key) return;
    lastInjectedRef.current = key;
    send(injectedPrompt.text);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectedPrompt]);

  // Toast handler (simple alert for now)
  const handleToast = useCallback((message, _variant) => {
    // In production: integrate with your toast system
    console.log(`[VUKZ Toast] ${_variant}: ${message}`);
  }, []);

  // request_selection: ref para quebrar dependência circular send ↔ handleSelection
  const handleSelectionRef = useRef(null);
  // Telemetria Audio v1: origem do conteúdo do input ('text'|'audio').
  // O workflow NUNCA muda por causa disso — só medição (taxa de reparo
  // do STT, erros por slot). Editar o transcript mantém 'audio'.
  const inputSourceRef = useRef('text');
  // LOAD_TARGET_CONTEXT: contexto curado do target selecionado, anexado
  // nos próximos envios como form_context.target_context (por formId).
  const targetContextRef = useRef(null);
  // Voice Conversation v1: refs declarados ANTES do send (sendRef é
  // atribuído logo após handleSelection — evitar TDZ no bundle).
  const [convoMode, setConvoMode] = useState(false);
  const convoModeRef = useRef(false);
  convoModeRef.current = convoMode;
  const sendRef = useRef(null);
  // RT2 — Realtime (WebRTC): Realtime = autoridade da CONVERSA (ouvir/falar/
  // barge-in). Cada pergunta vira 1 tool call vukz_assist_turn(text) →
  // /realtime/turn → MESMO send() (transport alternativo) → speech_text
  // volta como function_call_output mínimo. Fallback: STT v1 se OFF.
  const [realtimeStatus, setRealtimeStatus] = useState(RealtimeStatus.IDLE);
  const [realtimeEnabled, setRealtimeEnabled] = useState(null); // null = não checado
  const [realtimeEnv, setRealtimeEnv] = useState(null); // 'staging' quando gate ok
  const [realtimeTranscript, setRealtimeTranscript] = useState('');
  const realtimeClientRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const threadIdRef = useRef(null);
  // VOICE-FINAL (V15): 1 reconexão automática por queda (nova sessão Realtime,
  // MESMA thread canônica). Segunda queda seguida → fallback STT.
  const realtimeReconnectAtRef = useRef(0);
  // STAGING-2D (06/09/2026): guard de reentrância via REF, não via state.
  // O auto-continue do apply_selection dispara em setTimeout(0) logo após
  // o setLoading(false) do finally — mas handleSelectionRef.current ainda
  // pode fechar sobre um `send` memorizado com loading=true (re-render
  // pendente), e o `if (loading) return` derrubava o turno SILENCIOSAMENTE
  // (workflow morria em TARGET_RESOLVED). Ref é síncrono e não sofre disso.
  // setLoading continua existindo — mas só para a UI (spinner/disabled).
  const loadingRef = useRef(false);

  // ── Send Message ──────────────────────────────
  // `opts.transport` (RT2): transporte alternativo (voz Realtime) que devolve
  // o MESMO shape de sendAssistMessage — todo o pipeline canônico abaixo
  // (ASSIST v2, ACTION, FILL_FORM, seleção) é reutilizado byte-igual.
  const send = useCallback(async (text, opts = {}) => {
    if (!text.trim() || loadingRef.current) return null;
    // Contract v1 — "Desfaz.": resolvido LOCALMENTE quando há aplicação
    // reversível na instância ativa. O LLM não reconstrói valor anterior.
    if (UNDO_COMMAND_RE.test(text) && canUndo && canUndo()) {
      const u = undoLastAppliedProposal();
      const ok = u.status === 'undone';
      setMessages((prev) => [...prev,
        { id: Date.now(), role: 'user', text: text.trim() },
        { id: Date.now() + 1, role: 'assistant',
          text: ok ? T.undoDone : T.undoUnavailable,
          response: { type: 'CHAT', content: ok ? T.undoDone : T.undoUnavailable } },
      ]);
      setInputValue('');
      return null;
    }
    loadingRef.current = true;
    // Idempotência: o UUID pertence à MENSAGEM do usuário, não ao HTTP
    // request. Gerado aqui (submit) e reusado por qualquer retry — o
    // backend garante 1 thread / 1 run / 1 resposta por (user, UUID).
    const clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cmid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const userMsg = { id: Date.now(), role: 'user', text: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setLoading(true);
    setError(null);
    setShowPrompts(false);

    try {
      // Single Assist: sem prop formContext, usa o formulário ativo da
      // ponte (construído em TEMPO DE ENVIO — estado fresco do form).
      const bridgeForm = !formContext && getActiveForm ? getActiveForm() : null;
      // Contract v1 — captura NO ENVIO: formInstanceId, baseRevision, foco e
      // geração. A proposta que voltar herda ESTE contexto (nunca o da
      // chegada) — é o que torna stale detectável.
      const contractForm = !!(bridgeForm && typeof bridgeForm.applyOperations === 'function');
      const captured = bridgeForm && captureSendContext
        ? captureSendContext({ inputSource: inputSourceRef.current === 'audio' ? 'voice' : 'conversation' })
        : null;
      let effectiveFormContext = formContext
        || (bridgeForm?.getFormContext ? bridgeForm.getFormContext() : null);
      // LOAD_TARGET_CONTEXT: anexa o contexto curado do target selecionado
      // (carregado após o applySelection) — o agente preenche com DADOS.
      if (effectiveFormContext && targetContextRef.current?.formId === effectiveFormContext.form_id) {
        effectiveFormContext = { ...effectiveFormContext, target_context: targetContextRef.current.data };
      }
      // Contract v1 — foco capturado no envio ("aumenta ISSO para 10").
      // Contexto, nunca autorização: o adapter continua validando o path.
      if (effectiveFormContext && captured?.focus) {
        const { path, value, label, entity } = captured.focus;
        effectiveFormContext = {
          ...effectiveFormContext,
          focus: { path, value, label: label ?? null, entity: entity ?? null },
          form_instance_id: captured.formInstanceId,
          revision: captured.baseRevision,
        };
      } else if (effectiveFormContext && captured) {
        effectiveFormContext = { ...effectiveFormContext, form_instance_id: captured.formInstanceId, revision: captured.baseRevision };
      }
      const effectiveOnFillForm = onFillForm
        || (bridgeForm?.applyPatch ? bridgeForm.applyPatch : undefined);

      const transport = typeof opts.transport === 'function' ? opts.transport : sendAssistMessage;
      const result = await transport({
        message: text.trim(),
        threadId,
        role: normalizedRole,
        userId: user?.id,
        sport,
        currentRoute: location.pathname,
        executionContext,
        formContext: effectiveFormContext,
        // C4d-2: snapshot da rota ATUAL no momento do envio (nunca stale —
        // derivação pura, sem registro por página). Backend re-resolve.
        uiReferenceContext: buildUiReferenceContext(location.pathname),
        clientMessageId,
        inputSource: inputSourceRef.current,
      });
      inputSourceRef.current = 'text';

      // VOICE-FINAL (V6): turno cancelado por barge-in enquanto em voo — a
      // resposta chegou tarde. Nada é aplicado (thread/workspace/ações/CTA)
      // e nada é renderizado além do que o usuário disse.
      if (typeof opts.isCancelled === 'function' && opts.isCancelled()) {
        return null;
      }

      // Persist thread for multi-turn. PERF-2B (28/09/2026): o backend pode
      // ROLAR a thread Foundry (A→B) por limite de contexto e devolver o novo
      // id — adotar sempre que diferir. O histórico visual (messages) é local
      // e não muda; só o id físico. Persistência na assistSession acontece no
      // useEffect de [threadId].
      if (result.threadId && result.threadId !== threadId) {
        setThreadId(result.threadId);
      }

      // CANVAS-ENGINE-1: o Canvas é ESTADO — `workspace` vem em toda resposta
      // do coach e decide o que está visível (nenhuma ação open_canvas).
      if (applyWorkspaceRef.current && result.workspace !== undefined) {
        try { applyWorkspaceRef.current(result.workspace); } catch { /* fail-soft */ }
      }

      // Handle ACTION responses
      let actionResult = null;
      let v2Actions = null;
      if (result.response?.type === 'ASSIST') {
        // ── ASSIST v2 (C2): describe (puro) + efeitos só da allowlist.
        // navigate/link/created_execution NUNCA auto-executam — viram CTA.
        v2Actions = (result.response.actions || []).map((a) => ({
          action: a.action,
          payload: a.payload || {},
          navigation_execution: a.navigation_execution,
          desc: describeAction(a),
        }));
        v2Actions.forEach((va) => runImmediateEffect(va, { onToast: handleToast }));
        // C4d-3 (02/09/2026): auto-navigation EXPLÍCITA. Só executa quando
        // o BACKEND marcou navigation_execution.mode='auto' (composição
        // NAV-1 + destino whitelistado + referência C4d-2 + flag). Máximo
        // 1 por turno (double-guard — o backend já garante). O Workspace
        // central muda; o Assist permanece montado (shell C4d-1) — nada
        // de fechar painel, limpar conversa ou composer.
        const autoNav = v2Actions.find((va) => va.desc.kind === 'autoNavigate');
        if (autoNav) {
          setTimeout(() => navigate(autoNav.desc.route), 0);
        }
        // apply_selection continua no caminho mecânico confiável
        const auto = v2Actions.find((va) => va.desc.kind === 'autoSelection');
        if (auto) {
          const { field, option, context } = auto.desc.autoSelection;
          setTimeout(() => {
            if (handleSelectionRef.current) {
              handleSelectionRef.current(option, { field, context });
            }
          }, 0);
        }
      } else if (result.response?.type === 'ACTION') {
        // ── NAV-2 (01/09/2026): política de navegação centralizada.
        // Coach + navigate/link/created_execution no envelope LEGADO v1
        // vira CTA — desligar ASSIST_RESPONSE_V2_COACH não pode reativar
        // auto-navigation. Payload/label preservados: o usuário ainda
        // pode clicar e navegar (mesmo caminho onCta do v2).
        const navPolicy = navigationExecutionPolicy({
          role: normalizedRole,
          responseVersion: 'v1',
          action: result.response.action,
        });
        if (navPolicy === 'cta') {
          const desc = describeAction({
            action: result.response.action,
            payload: result.response.payload || {},
          });
          if (desc.kind === 'cta') {
            v2Actions = [{
              action: result.response.action,
              payload: result.response.payload || {},
              desc,
            }];
          }
          // desc ignorada (ex.: navigate sem route) → nada a executar;
          // NUNCA fallback para executeAction em action de navegação.
        } else {
          actionResult = executeAction(result.response, navigate, handleToast);
        }
      }

      // Pipeline TARGET (27/08): apply_selection — o workflow resolveu o
      // target server-side (texto livre → ID canônico). Mesmo caminho do
      // clique no chip: applySelection na ponte + target_context + navegação.
      if (actionResult?.autoSelection) {
        const { field, option, context } = actionResult.autoSelection;
        setTimeout(() => {
          if (handleSelectionRef.current) {
            handleSelectionRef.current(option, { field, context });
          }
        }, 0);
      }

      const hasV2FillForm = v2Actions?.some((va) => va.desc.kind === 'fillForm');
      const hasV2Selection = v2Actions?.some(
        (va) => va.desc.kind === 'selection' || (va.desc.kind === 'fillForm' && va.desc.selection)
      );

      // Contract v1 — compatibility: fill_form (patch plano) → Proposal com
      // operations[], herdando o contexto capturado no send. Só quando o
      // formulário ativo NO ENVIO declarou applyOperations; os demais seguem
      // no FillFormBubble legado sem mudança de comportamento.
      let legacyProposal = null;
      if (contractForm && captured && proposalFromFillForm) {
        if (v2Actions) {
          v2Actions.forEach((va) => {
            if (va.desc.kind === 'fillForm') va.proposal = proposalFromFillForm(va.desc.fillForm, captured);
          });
        }
        if (actionResult?.fillForm) legacyProposal = proposalFromFillForm(actionResult.fillForm, captured);
      }

      const assistMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        text: result.response?.message || result.response?.content || '',
        response: result.response,
        meta: result.meta,
        actionResult,
        v2Actions,
        // Contract v1: proposta (envelope) do caminho legado v1
        proposal: legacyProposal,
        // ASSIST v2: CTAs só executam no CLIQUE — nunca no recebimento.
        onCta: v2Actions
          ? (cta) => {
              if (cta?.route) navigate(cta.route);
              else if (cta?.url) window.open(cta.url, '_blank', 'noopener,noreferrer');
            }
          : undefined,
        // FILL_FORM v1: callback de confirmação (só executa quando o
        // usuário clicar em Aplicar no FillFormBubble)
        onFillForm: (actionResult?.fillForm || hasV2FillForm) ? effectiveOnFillForm : undefined,
        // request_selection: click no chip → handleSelection (via ref —
        // handleSelection depende de send, definido depois)
        onSelect: (actionResult?.selection || hasV2Selection)
          ? (opt, sel) => handleSelectionRef.current && handleSelectionRef.current(opt, sel)
          : undefined,
      };
      setMessages((prev) => [...prev, assistMsg]);
      return result;

    } catch (err) {
      const cancelled = err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED'
        || (typeof opts.isCancelled === 'function' && opts.isCancelled());
      if (cancelled) {
        // Barge-in: silêncio — sem bolha de erro, sem banner.
        if (opts.rethrow) throw err;
        return null;
      }
      console.error('[VukzAssistPanel] Error:', err);
      setError(err.message || T.errorComm);
      const errorMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        text: T.errorMsg,
        response: { type: 'CHAT', content: T.errorContent },
      };
      setMessages((prev) => [...prev, errorMsg]);
      if (opts.rethrow) throw err;
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [loading, threadId, normalizedRole, user, sport, location.pathname, navigate, handleToast, T, executionContext, formContext, onFillForm, getActiveForm,
    captureSendContext, proposalFromFillForm, canUndo, undoLastAppliedProposal]);

  // ── Seleção de opção (request_selection) ─────────────
  // IDs vêm do backend. A aplicação ao formulário é via ponte CONFIÁVEL
  // (applySelection do form) — nunca via patch do LLM (que rejeita ids).
  const handleSelection = useCallback((opt, selection) => {
    const form = getActiveForm ? getActiveForm() : null;
    // Multi-select (SETUP_CLUB): opt pode ser ARRAY de opções.
    const isMulti = Array.isArray(opt);

    // Pipeline TARGET (27/08): fluxo iniciado FORA do builder (/assist, Home).
    // Sem formulário ativo + workflow de treino → guarda o target (ID já
    // validado pelo backend) e navega; o builder materializa no mount.
    const selCtx = selection.context || {};
    if (!form && !isMulti && opt?.id && selCtx.workflow === 'CREATE_TRAINING'
        && selCtx.navigate_to) {
      try {
        sessionStorage.setItem('vukz_pending_target', JSON.stringify({
          field: selection.field,
          option: opt,
          requested_volume_m: selCtx.requested_volume_m || null,
          ts: Date.now(),
        }));
      } catch (e) { /* sessionStorage indisponível — segue sem materializar */ }
      navigate(selCtx.navigate_to);
      // target_context continua sendo carregado para o próximo envio
      fetchTargetContext('CREATE_TRAINING', { type: opt.type, id: opt.id }).then((data) => {
        if (data) targetContextRef.current = { formId: 'training_builder', data };
      });
      const kindNav = opt.type === 'squad' || opt.type === 'group' ? 'o grupo' : 'o atleta';
      send(
        `Selecionei ${kindNav} ${opt.label} (${opt.type}:${opt.id}).`
        + (selCtx.requested_volume_m ? ` Volume alvo: ${selCtx.requested_volume_m}m.` : '')
        + ' Qual o próximo passo?'
      );
      return;
    }

    // Chips genéricos de workflow (etapas, tipo de bloco, volume, decisão):
    // não são campos do formulário — só devolvem a escolha ao backend, que
    // possui o turno (pending workflow). Padrão "(choice:id)" é o contrato.
    if (!isMulti && opt?.type === 'choice') {
      send(`${opt.label} (choice:${opt.id})`);
      return;
    }

    if (form?.applySelection) {
      try { form.applySelection(selection.field, opt); }
      catch (e) { console.warn('[VukzAssistPanel] applySelection error:', e); }
    }
    // LOAD_TARGET_CONTEXT: busca contexto curado (fire-and-forget fail-soft)
    // e guarda por formId — próximo envio anexa em form_context.target_context.
    const WORKFLOW_BY_FORM = {
      training_builder: 'CREATE_TRAINING',
      physical_training_builder: 'CREATE_PHYSICAL_TRAINING',
      nutrition_plan: 'CREATE_NUTRITION_PLAN',
      club_setup: 'SETUP_CLUB',
    };
    const wf = form?.formId ? WORKFLOW_BY_FORM[form.formId] : null;
    // SETUP_CLUB: o target é o próprio clube (id vem do token no backend).
    if (wf === 'SETUP_CLUB') {
      fetchTargetContext(wf, { type: 'club' }).then((data) => {
        if (data) targetContextRef.current = { formId: form.formId, data };
      });
    } else if (wf && !isMulti && opt?.id) {
      fetchTargetContext(wf, { type: opt.type, id: opt.id }).then((data) => {
        if (data) targetContextRef.current = { formId: form.formId, data };
      });
    }
    if (isMulti) {
      const labels = opt.map((o) => o.label).join(', ');
      send(
        `Selecionei ${opt.length} para ${selection.field}: ${labels}. Qual o próximo passo?`
      );
      return;
    }
    const volume = selection.context?.requested_volume_m;
    const kind = opt.type === 'squad' || opt.type === 'group' ? 'o grupo' : 'o atleta';
    // Genérico por formulário (treino, plano nutricional, ...) — o backend
    // sabe o workflow pelo form_context; aqui só materializamos a escolha.
    send(
      `Selecionei ${kind} ${opt.label} (${opt.type}:${opt.id}).`
      + (volume ? ` Volume alvo: ${volume}m.` : '')
      + ' Qual o próximo passo?'
    );
  }, [getActiveForm, send, navigate]);
  handleSelectionRef.current = handleSelection;
  sendRef.current = send;
  // CANVAS-ENGINE-1: Canvas responde ao switch guard via chat normal.
  useEffect(() => {
    const reg = assistCtx?.registerCanvasSender;
    if (!reg) return undefined;
    reg((text, opts) => sendRef.current?.(text, opts));
    return () => reg(null);
  }, [assistCtx]);
  threadIdRef.current = threadId;

  // ── RT2 — Realtime lifecycle ────────────────────────────────────
  const stopRealtime = useCallback(() => {
    try { realtimeClientRef.current?._sessionAbort?.abort(); } catch (_) { /* ignore */ }
    realtimeClientRef.current?.close();
    realtimeClientRef.current = null;
    setRealtimeTranscript('');
  }, []);

  const startRealtime = useCallback(async () => {
    if (realtimeClientRef.current) return;
    setError(null);
    // RT4.0-FIX: nova sessão debug começa limpa — evita misturar amostras de
    // sessões anteriores da mesma aba no baseline.
    try {
      if (localStorage.getItem('vukz_rt_debug') === '1') {
        window.__VUKZ_RT_EVENTS = [];
        window.__VUKZ_RT_TIMINGS = [];
        window.__VUKZ_RT_BARGEINS = [];
      }
    } catch (_) { /* ignore */ }
    const sessionAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const client = new RealtimeClient({
      audioElement: remoteAudioRef.current,
      // PERF-1: criação da sessão é abortável — "cancelar" durante Conectando…
      // interrompe o HTTP em vez de deixar a promessa pendurada.
      createSession: () => createRealtimeSession({ signal: sessionAbort?.signal }),
      onStatus: (st) => setRealtimeStatus(st),
      onTranscript: (t) => { if (t.role === 'user') setRealtimeTranscript(t.text); },
      // Observer DevTools (opt-in): localStorage.vukz_rt_debug='1' →
      // window.__VUKZ_RT_EVENTS (só type/call_id/response_id; nunca ek_/áudio).
      onEvent: (dir, e) => {
        try {
          if (localStorage.getItem('vukz_rt_debug') !== '1') return;
          const row = { t: new Date().toISOString(), dir, ...e };
          (window.__VUKZ_RT_EVENTS = window.__VUKZ_RT_EVENTS || []).push(row);
          if (!/\.delta$/.test(e.type || '')) console.debug('[RT]', dir, e.type, e.call_id || '', e.response_id || '');
        } catch (_) { /* ignore */ }
      },
      // RT4.0 — latência por turno (marcos monotônicos + meta.timings do
      // backend). Mesmo opt-in do observer; só números, nunca texto/áudio.
      onTiming: (report) => {
        try {
          if (localStorage.getItem('vukz_rt_debug') !== '1') return;
          const row = { t: new Date().toISOString(), ...report };
          (window.__VUKZ_RT_TIMINGS = window.__VUKZ_RT_TIMINGS || []).push(row);
          console.debug('[RT timing]', report.call_id || '', report.latencies, report.server_timings || '');
        } catch (_) { /* ignore */ }
      },
      // RT4.1 — evidência de barge-in (response_id/item_id/audio_end_ms; só ids e números).
      onBargeIn: (report) => {
        try {
          if (localStorage.getItem('vukz_rt_debug') !== '1') return;
          (window.__VUKZ_RT_BARGEINS = window.__VUKZ_RT_BARGEINS || []).push({ t: new Date().toISOString(), ...report });
          console.debug('[RT barge-in]', report.truncated ? 'truncated' : `skipped:${report.skip_reason}`, report.item_id || '', report.audio_end_ms);
        } catch (_) { /* ignore */ }
      },
      onError: (err) => {
        console.warn('[VukzAssistPanel] Realtime error:', err);
        const msg = String(err?.message || '');
        const peerDrop = /^peer_|datachannel_error/.test(msg);
        const now = Date.now();
        if (peerDrop && realtimeClientRef.current === client && (now - realtimeReconnectAtRef.current) > 30000) {
          // V15: conexão caiu → reconecta UMA vez; a thread fica em threadIdRef /
          // messages (estado do painel) e o backend deduplica o último comando.
          realtimeReconnectAtRef.current = now;
          setError(null);
          client.close();
          realtimeClientRef.current = null;
          setTimeout(() => { startRealtimeRef.current?.(); }, 500);
          return;
        }
        setError('Voz em tempo real indisponível — usando transcrição.');
      },
      // Tool call → MESMO send() do teclado/STT, com transport de voz.
      // O texto é o ARGUMENTO da tool (não o transcript ASR).
      onToolCall: async (text, meta = {}) => {
        inputSourceRef.current = 'audio';
        const result = await sendRef.current?.(text, {
          rethrow: true,
          // VOICE-FINAL (V6): barge-in aborta o HTTP e invalida o resultado —
          // nenhuma ação/CTA/workspace de um turno cancelado é aplicada.
          signal: meta.signal,
          isCancelled: meta.isCancelled,
          transport: ({ message, threadId: tid, clientMessageId, currentRoute, sport: sp }) =>
            sendRealtimeTurn({ text: message, threadId: tid ?? threadIdRef.current, clientMessageId, currentRoute, sport: sp, signal: meta.signal }),
        });
        if (result == null) return null;
        return {
          speech_text: result?.speechText || result?.response?.message || result?.response?.content || '',
          timings: result?.meta?.timings || null, // RT4.0 (observabilidade)
        };
      },
    });
    realtimeClientRef.current = client;
    client._sessionAbort = sessionAbort;
    try {
      await client.connect();
    } catch (err) {
      realtimeClientRef.current = null;
      const cancelled = err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' || client.closed;
      if (!cancelled) setRealtimeEnabled(false); // fallback STT v1 nesta sessão (não quando o usuário cancelou)
    }
  }, []);
  const startRealtimeRef = useRef(null);
  startRealtimeRef.current = startRealtime;

  // convoMode ON → (1ª vez) health; se enabled → Realtime; senão STT v1.
  useEffect(() => {
    let cancelled = false;
    if (!convoMode) { stopRealtime(); return undefined; }
    if (!isRealtimeSupported()) { setRealtimeEnabled(false); return undefined; }
    (async () => {
      let enabled = realtimeEnabled;
      if (enabled === null) {
        const h = await checkRealtimeHealth();
        if (cancelled) return;
        enabled = h.enabled;
        setRealtimeEnv(h.enabled ? h.environment : null);
        if (!h.enabled) console.info('[VukzAssistPanel] Realtime gate:', h.gate);
        setRealtimeEnabled(enabled);
      }
      if (enabled) await startRealtime();
    })();
    return () => { cancelled = true; };
  }, [convoMode, realtimeEnabled, startRealtime, stopRealtime]);

  useEffect(() => () => stopRealtime(), [stopRealtime]);
  const realtimeActive = convoMode && !!realtimeClientRef.current && realtimeStatus !== RealtimeStatus.IDLE;

  // ── Audio Input v1 (STT-only) ──────────────────────
  // Áudio é somente outro input transport: mic → MediaRecorder →
  // /vukz/assist/transcribe → transcript no INPUT (editável) → o coach
  // revisa e envia pelo MESMO handleSubmit/send (mesmo client_message_id,
  // thread, orchestrator, pending_workflow). Falha de áudio nunca quebra
  // o chat escrito — só mostra o banner de erro.
  const micSupported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof window !== 'undefined' && !!window.MediaRecorder;
  // Voice Conversation v1: duas modalidades no MESMO painel.
  //   NORMAL:   áudio → transcript editável no input → envio manual
  //   CONVERSA: áudio → STT → transcript vira mensagem do usuário e é
  //             enviado automaticamente pelo MESMO send() (mesma thread,
  //             mesmo pending_workflow, mesmo client_message_id por
  //             utterance, input_source='audio'). Chip, teclado e voz
  //             produzem exatamente o mesmo resultado no workflow.
  // Sem wake word, sem listening contínuo, sem streaming, sem TTS (v2).
  const [recState, setRecState] = useState('idle'); // idle | recording | transcribing
  const [recSeconds, setRecSeconds] = useState(0);
  const recorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recCancelledRef = useRef(false);
  const recTimerRef = useRef(null);

  const stopRecTimer = () => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
  };

  const startRecording = useCallback(async () => {
    if (recState !== 'idle' || loading) return;
    setError(null);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      setError('Permissão de microfone negada — use o teclado normalmente.');
      return;
    }
    try {
      const mime = MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported?.('audio/mp4') ? 'audio/mp4' : '');
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recChunksRef.current = [];
      recCancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data?.size) recChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        stopRecTimer();
        if (recCancelledRef.current) { setRecState('idle'); setRecSeconds(0); return; }
        const blob = new Blob(recChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setRecSeconds(0);
        if (!blob.size) { setRecState('idle'); setError('Áudio vazio — tente de novo.'); return; }
        setRecState('transcribing');
        try {
          const text = await transcribeAudio(blob);
          inputSourceRef.current = 'audio';
          if (convoModeRef.current && text?.trim()) {
            // MODO CONVERSA: transcript aparece como mensagem do usuário e
            // é enviado automaticamente pelo MESMO send() — zero caminhos
            // novos: mesma thread, mesmo orchestrator, mesmo pending.
            setRecState('idle');
            sendRef.current?.(text.trim());
            return;
          }
          // MODO NORMAL: transcript vai para o INPUT editável — o coach
          // revisa nomes, metros e tempos antes de enviar. Só o texto
          // editado chega ao orchestrator (nunca o áudio).
          setInputValue((prev) => (prev ? `${prev} ${text}` : text));
          inputRef.current?.focus();
        } catch (err) {
          console.warn('[VukzAssistPanel] Transcribe error:', err);
          setError(err.message || 'Não consegui transcrever — digite normalmente.');
        } finally {
          setRecState('idle');
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecState('recording');
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        setRecSeconds((s) => {
          // Limite v1: utterances curtas (90s) — para sozinho.
          if (s + 1 >= 90 && recorderRef.current?.state === 'recording') {
            recorderRef.current.stop();
          }
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      setError('Não consegui iniciar a gravação neste navegador.');
    }
  }, [recState, loading]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  const cancelRecording = useCallback(() => {
    // Cancelar → zero chamada ao chat/transcrição.
    recCancelledRef.current = true;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  useEffect(() => () => {
    // Unmount durante gravação: descarta tudo.
    recCancelledRef.current = true;
    if (recorderRef.current?.state === 'recording') { try { recorderRef.current.stop(); } catch (_) {} }
    stopRecTimer();
  }, []);

  // ── Handle Submit ─────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    send(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(inputValue);
    }
  };

  // ── Reset conversation ────────────────────────
  const handleReset = useCallback(async () => {
    // Única forma de criar nova conversa (Single Assist): limpa a sessão
    // compartilhada — drawer//assist/home recomeçam juntos.
    clearSession(normalizedRole);
    // For roles with persistent threads: reset in backend DB too
    if (hasThreadPersistence && user?.id && threadId) {
      try {
        const result = await resetAssistThread(normalizedRole, user.id, sport);
        setThreadId(result.threadId);
      } catch (err) {
        console.warn('[VukzAssistPanel] Backend reset failed, resetting locally:', err);
        setThreadId(null);
      }
    } else {
      setThreadId(null);
    }
    setMessages([]);
    setError(null);
    setShowPrompts(true);
    inputRef.current?.focus();
  }, [hasThreadPersistence, user, threadId, normalizedRole, sport]);

  // ── Quick Prompt click ────────────────────────
  const handlePromptClick = (text) => {
    send(text);
  };

  return (
    <div
      className="rounded-xl border bg-white flex flex-col overflow-hidden"
      style={{
        borderColor: VUKZ.border,
        height: fluid ? '100%' : '520px',
        maxHeight: fluid ? '100%' : '70vh',
      }}
    >
      {/* ─── Header ─── */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between shrink-0"
        style={{ borderColor: VUKZ.border }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: VUKZ.primaryLight }}
          >
            <Sparkles size={14} style={{ color: VUKZ.primary }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: VUKZ.text }}>
              VUKZ Assist
            </h3>
            <p className="text-[10px] leading-tight" style={{ color: VUKZ.textLight }}>
              Swim · {roleLabel}
              {!formContext && activeFormId && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">
                  ✨ formulário ativo
                </span>
              )}
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={handleReset}
            className="p-1.5 rounded-md hover:bg-neutral-100 transition-colors"
          title={T.titleReset}
          >
            <RotateCcw size={13} style={{ color: VUKZ.textLight }} />
          </button>
        )}
      </div>

      {/* ─── Messages Area ─── */}
      <div
        className="flex-1 overflow-y-auto px-3 py-3"
        style={{ backgroundColor: '#fafafa' }}
      >
        {/* Empty state with quick prompts */}
        {messages.length === 0 && showPrompts && (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <div className="text-center">
              <MessageCircle size={28} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm font-medium" style={{ color: VUKZ.text }}>
                {T.emptyState[roleDisplayKey]}
              </p>
              <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>
                {T.evidence}
              </p>
            </div>

            <div className="w-full grid grid-cols-2 gap-2">
              {prompts.map((prompt) => {
                const Icon = prompt.icon;
                return (
                  <button
                    key={prompt.label}
                    onClick={() => handlePromptClick(prompt.text)}
                    className="text-left px-3 py-2.5 rounded-lg border bg-white hover:bg-neutral-50 hover:border-neutral-300 transition-all text-xs group"
                    style={{ borderColor: VUKZ.border }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon
                        size={12}
                        className="shrink-0 transition-colors"
                        style={{ color: VUKZ.textLight }}
                      />
                      <span className="font-medium truncate" style={{ color: VUKZ.text }}>
                        {prompt.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Message list */}
        {messages.map((msg, idx) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            onFeedback={(m, type) => {
              submitAssistFeedback({
                agentName: agentNameForRole,
                feedbackType: type,
                threadId: threadId,
                messageIndex: idx,
                userId: user?.id,
                role: normalizedRole,
                clubId: user?.club_id,
              }).catch(err => console.warn('[Feedback] Error:', err));
            }}
          />
        ))}

        {/* Typing indicator */}
        {loading && <TypingIndicator />}

        {/* Error banner */}
        {error && !loading && (
          <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-red-50 border border-red-200">
            <AlertCircle size={14} className="text-red-400 shrink-0" />
            <span className="text-xs text-red-600">{error}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ─── Input Area (sticky bottom) ─── */}
      <form
        onSubmit={handleSubmit}
        className="px-3 py-2.5 border-t shrink-0"
        style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
      >
        <div
          className="flex items-center gap-2 rounded-lg border px-3 py-1.5 focus-within:border-neutral-400 transition-colors"
          style={{ borderColor: recState === 'recording' ? '#ef4444' : VUKZ.border }}
        >
          {micSupported && recState === 'idle' && (
            <>
              <button
                type="button"
                onClick={() => setConvoMode((v) => !v)}
                disabled={loading}
                title={realtimeActive && realtimeStatus === RealtimeStatus.CONNECTING
                  ? 'Conectando à voz… clique para cancelar'
                  : convoMode ? 'Modo conversa ATIVO — transcrição envia automaticamente. Clique para voltar ao modo normal.' : 'Ativar modo conversa (voz envia automaticamente)'}
                aria-busy={realtimeActive && realtimeStatus === RealtimeStatus.CONNECTING ? 'true' : undefined}
                className="p-1.5 rounded-md transition-colors disabled:opacity-30"
                style={{
                  backgroundColor: convoMode ? VUKZ.primary : 'transparent',
                  color: convoMode ? '#fff' : VUKZ.textLight,
                }}
              >
                <AudioLines size={14} />
              </button>
              {realtimeActive ? (
                <div className="flex items-center gap-1.5 shrink-0" title={`Realtime: ${realtimeStatus}`}>
                  {realtimeEnv && (
                    <span className="text-[9px] font-bold px-1 rounded uppercase tracking-wide" style={{ backgroundColor: '#fef3c7', color: '#92400e' }} title="Piloto Realtime — ambiente">
                      {realtimeEnv}
                    </span>
                  )}
                  {realtimeStatus === RealtimeStatus.CONNECTING || realtimeStatus === RealtimeStatus.THINKING ? (
                    <Loader2 size={13} className="animate-spin" style={{ color: VUKZ.primary }} />
                  ) : (
                    <span className={`w-2 h-2 rounded-full ${realtimeStatus === RealtimeStatus.SPEAKING ? 'bg-amber-500 animate-pulse' : realtimeStatus === RealtimeStatus.ERROR ? 'bg-red-500' : 'bg-emerald-500 animate-pulse'}`} />
                  )}
                  <span className="text-[11px]" style={{ color: VUKZ.textLight }}>
                    {{ connecting: 'Conectando à voz…', listening: 'Ouvindo', thinking: 'Consultando…', speaking: 'Falando', error: 'Erro' }[realtimeStatus] || ''}
                  </span>
                  {realtimeStatus === RealtimeStatus.CONNECTING && (
                    <button type="button" onClick={() => setConvoMode(false)}
                      className="text-[11px] underline" style={{ color: VUKZ.textLight }}>
                      cancelar
                    </button>
                  )}
                </div>
              ) : (
              <button
                type="button"
                onClick={startRecording}
                disabled={loading}
                title={convoMode ? 'Falar — envia ao parar' : 'Falar com o VUKZ Assist'}
                className="p-1.5 rounded-md transition-colors hover:bg-neutral-100 disabled:opacity-30"
              >
                <Mic size={14} style={{ color: convoMode ? VUKZ.primary : VUKZ.textLight }} />
              </button>
              )}
            </>
          )}
          {recState === 'recording' && (
            <div className="flex items-center gap-2 shrink-0">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-medium tabular-nums text-red-600">
                {String(Math.floor(recSeconds / 60)).padStart(2, '0')}:{String(recSeconds % 60).padStart(2, '0')}
              </span>
              <button type="button" onClick={stopRecording} title="Parar e transcrever"
                className="p-1.5 rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors">
                <Square size={12} />
              </button>
              <button type="button" onClick={cancelRecording} title="Cancelar gravação"
                className="p-1.5 rounded-md hover:bg-neutral-100 transition-colors">
                <X size={13} style={{ color: VUKZ.textLight }} />
              </button>
            </div>
          )}
          {recState === 'transcribing' && (
            <div className="flex items-center gap-1.5 shrink-0">
              <Loader2 size={13} className="animate-spin" style={{ color: VUKZ.textLight }} />
              <span className="text-[11px]" style={{ color: VUKZ.textLight }}>Transcrevendo…</span>
            </div>
          )}
          <input
            ref={inputRef}
            type="text"
            placeholder={recState === 'recording'
              ? (convoMode ? 'Gravando… ao parar, envia automaticamente' : 'Gravando… fale a instrução')
              : (convoMode ? (realtimeActive ? (realtimeTranscript || 'Conversa por voz ativa — fale ou digite') : 'Modo conversa: fale ou digite') : (T.placeholder[roleDisplayKey] || T.placeholder.TREINADOR))}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading || recState !== 'idle'}
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-gray-400 disabled:opacity-50"
            style={{ color: VUKZ.text }}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || loading}
            className="p-1.5 rounded-md transition-colors disabled:opacity-30"
            style={{
              backgroundColor: inputValue.trim() ? VUKZ.primary : 'transparent',
              color: inputValue.trim() ? '#fff' : VUKZ.textLight,
            }}
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
          </button>
        </div>
        <p className="text-[10px] mt-1.5 text-center" style={{ color: convoMode ? VUKZ.primary : VUKZ.textLight }}>
          {convoMode
            ? (realtimeActive ? '🎙 Conversa em tempo real — pode interromper a qualquer momento' : '🎙 Modo conversa — fale, o VUKZ responde, fale de novo')
            : T.footer}
        </p>
        {/* RT2: sink de áudio remoto (voz do Realtime). Sempre montado para o ref existir. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
      </form>

      {/* ─── Inline styles for markdown rendering ─── */}
      <style>{`
        .assist-markdown { line-height: 1.6; }
        .assist-markdown .assist-h2 { font-size: 1rem; font-weight: 700; margin: 0.75rem 0 0.25rem; }
        .assist-markdown .assist-h3 { font-size: 0.875rem; font-weight: 700; margin: 0.5rem 0 0.25rem; }
        .assist-markdown .assist-h4 { font-size: 0.8125rem; font-weight: 600; margin: 0.5rem 0 0.125rem; }
        .assist-markdown .assist-p { margin: 0.25rem 0; }
        .assist-markdown .assist-ul,
        .assist-markdown .assist-ol { margin: 0.25rem 0; padding-left: 1.25rem; }
        .assist-markdown .assist-li { margin: 0.125rem 0; list-style: disc; }
        .assist-markdown .assist-li-ordered { margin: 0.125rem 0; list-style: decimal; }
        .assist-markdown .assist-inline-code {
          background: #f3f4f6; padding: 0.125rem 0.375rem; border-radius: 0.25rem;
          font-size: 0.75rem; font-family: monospace;
        }
        .assist-markdown .assist-code-block {
          background: #1f2937; color: #e5e7eb; padding: 0.75rem; border-radius: 0.5rem;
          font-size: 0.7rem; font-family: monospace; overflow-x: auto; margin: 0.5rem 0;
        }
        .assist-markdown .assist-link {
          color: #111; text-decoration: underline; text-underline-offset: 2px;
        }
        .assist-markdown .assist-hr {
          border: none; border-top: 1px solid #e5e7eb; margin: 0.5rem 0;
        }
        .assist-markdown strong { font-weight: 600; }
      `}</style>
    </div>
  );
};

export default VukzAssistPanel;
